'use client';

import * as Sentry from '@sentry/nextjs';
import { useEffect } from 'react';
import { Button } from '@vm/ui';

interface ErrorProps {
  error: Error & { digest?: string };
  reset: () => void;
}

export default function Error({ error, reset }: ErrorProps) {
  useEffect(() => {
    // Report the error to Sentry with additional context
    Sentry.captureException(error, {
      tags: {
        component: 'RouteError',
        app: 'repetix',
        route: typeof window !== 'undefined' ? window.location.pathname : 'unknown',
      },
      contexts: {
        error: {
          digest: error.digest,
          message: error.message,
          name: error.name,
        },
        page: {
          url: typeof window !== 'undefined' ? window.location.href : 'unknown',
          referrer: typeof window !== 'undefined' ? document.referrer : 'unknown',
        },
      },
      level: 'error',
    });
  }, [error]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6 text-center">
        <div className="w-16 h-16 mx-auto mb-4 bg-orange-100 rounded-full flex items-center justify-center">
          <svg
            className="w-8 h-8 text-orange-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>
        
        <h1 className="text-xl font-semibold text-gray-900 mb-2">
          Page Error
        </h1>
        
        <p className="text-gray-600 mb-6">
          There was an issue loading this page. This has been reported to our team.
        </p>
        
        {error.digest && (
          <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded">
            <p className="text-sm text-gray-600 mb-1">Error Reference:</p>
            <code className="text-xs text-gray-800 bg-white px-2 py-1 rounded border">
              {error.digest}
            </code>
          </div>
        )}
        
        <div className="space-y-3">
          <Button
            onClick={reset}
            className="w-full"
            variant="primary"
          >
            Try Again
          </Button>
          
          <Button
            onClick={() => window.history.back()}
            variant="secondary"
            className="w-full"
          >
            Go Back
          </Button>

          <Button
            onClick={() => window.location.href = '/dashboard'}
            variant="secondary"
            className="w-full"
          >
            Go to Dashboard
          </Button>
        </div>
        
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-500">
            Need help?{' '}
            <a
              href="mailto:info@vanmoose.com"
              className="text-blue-600 hover:text-blue-700 underline"
            >
              Contact Support
            </a>
          </p>
        </div>

        {process.env.NODE_ENV === 'development' && (
          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded text-left">
            <p className="text-sm text-yellow-800 font-medium mb-2">Development Error:</p>
            <p className="text-xs text-yellow-700 mb-2">
              <strong>Message:</strong> {error.message}
            </p>
            {error.stack && (
              <details className="mt-2">
                <summary className="text-xs text-yellow-600 cursor-pointer">Stack Trace</summary>
                <pre className="text-xs text-yellow-600 mt-1 whitespace-pre-wrap break-words max-h-32 overflow-y-auto">
                  {error.stack}
                </pre>
              </details>
            )}
          </div>
        )}
      </div>
    </div>
  );
}